import { Button } from '@/components/ui/button';
import { Search, ArrowRight, MessageCircle, ChevronLeft, ChevronRight, Car, Video, CreditCard, Shield, ShoppingCart, FileText, Wrench, Users, Package, Settings, Gauge, Handshake, HandCoins, TrendingUp, Calendar, Fuel, Palette, Bike, Truck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { getTransparentBrandLogo } from '@/lib/brandLogos';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import { useLayout7Theme } from './theme';
import { useAppData } from '@/contexts/AppDataContext';
import { createPortal } from 'react-dom';
import { generateVehicleSlug } from '@/lib/slug';
import { ServiceModal } from '@/components/ServiceModal';
import { useStore } from '@/contexts/StoreContext';
import { Building2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import './home.css';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

function formatFinderPrice(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function FinderBrandButton({
  brand,
  active,
  hidden,
  onSelect,
}: {
  brand: string;
  active: boolean;
  hidden?: boolean;
  onSelect: (brand: string) => void;
}) {
  const [failed, setFailed] = useState(false);
  const logo = getTransparentBrandLogo(brand);
  const showLogo = Boolean(logo) && !failed;

  return (
    <button
      type="button"
      className={`wl7-finder-brand${active ? ' is-active' : ''}`}
      aria-pressed={hidden ? undefined : active}
      data-brand={brand}
      aria-label={brand}
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : 0}
      onClick={() => onSelect(brand)}
    >
      {showLogo ? (
        <img src={logo} alt="" draggable={false} onError={() => setFailed(true)} />
      ) : (
        <span className="wl7-finder-brand-fallback">{brand}</span>
      )}
    </button>
  );
}

function FinderBrandCarousel({
  brands,
  activeBrand,
  onSelect,
}: {
  brands: string[];
  activeBrand: string;
  onSelect: (brand: string) => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(Number.POSITIVE_INFINITY);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [instant, setInstant] = useState(false);
  const drag = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    lastX: 0,
    lastY: 0,
    moved: false,
  });
  const suppressClick = useRef(false);
  const stopDragListeners = useRef<() => void>(() => {});
  const indexRef = useRef(0);
  const stepRef = useRef(0);
  const maxTranslateIndexRef = useRef(0);
  const onSelectRef = useRef(onSelect);
  const canSlide = brands.length > 1 && visible < brands.length;
  const looped = canSlide ? [...brands, ...brands, ...brands] : brands;
  const maxTranslateIndex = Math.max(0, looped.length - Math.min(visible, looped.length));
  stepRef.current = step;
  maxTranslateIndexRef.current = maxTranslateIndex;
  onSelectRef.current = onSelect;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const measure = () => {
      const item = viewport.querySelector<HTMLElement>('.wl7-finder-brand');
      const track = viewport.querySelector<HTMLElement>('.wl7-finder-marquee-track');
      if (!item || !track) return;
      const gap = Number.parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      const nextStep = item.offsetWidth + gap;
      setStep(nextStep);
      setVisible(Math.max(1, Math.floor((viewport.clientWidth + gap) / nextStep)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [brands, canSlide]);

  useEffect(() => {
    if (!canSlide) {
      indexRef.current = 0;
      setIndex(0);
      return;
    }
    const base = brands.length;
    const current = indexRef.current;
    if (current >= base && current < base * 2) return;
    setInstant(true);
    indexRef.current = base;
    setIndex(base);
  }, [canSlide, brands.length]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    if (!instant) return;
    const frame = requestAnimationFrame(() => setInstant(false));
    return () => cancelAnimationFrame(frame);
  }, [instant, index]);

  useEffect(() => {
    if (dragging || !canSlide) return;
    const base = brands.length;
    if (index >= base && index < base * 2) return;
    const timer = window.setTimeout(() => {
      const normalized = ((index % base) + base) % base + base;
      setInstant(true);
      indexRef.current = normalized;
      setIndex(normalized);
    }, 360);
    return () => window.clearTimeout(timer);
  }, [index, dragging, canSlide, brands.length]);

  useEffect(() => {
    if (dragging || !canSlide) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setInterval(() => {
      const next = indexRef.current + 1;
      indexRef.current = next;
      setIndex(next);
    }, 2200);

    return () => window.clearInterval(timer);
  }, [dragging, canSlide]);

  const endDrag = (clientX: number, clientY: number, select: boolean) => {
    if (!drag.current.active) return;
    const delta = clientX - drag.current.startX;
    const moved = drag.current.moved;
    drag.current.active = false;
    drag.current.pointerId = -1;
    stopDragListeners.current();
    stopDragListeners.current = () => {};
    setDragging(false);

    if (moved || !select) {
      if (moved) suppressClick.current = true;
      const stepNow = stepRef.current;
      const shift = stepNow > 0 ? Math.round(-delta / stepNow) : 0;
      setDragX(0);
      if (shift !== 0) {
        setIndex((current) => {
          const next = Math.max(0, Math.min(maxTranslateIndexRef.current, current + shift));
          indexRef.current = next;
          return next;
        });
      }
      return;
    }

    setDragX(0);
    const hit = document.elementFromPoint(clientX, clientY);
    const brand = hit?.closest<HTMLElement>('.wl7-finder-brand')?.dataset.brand;
    if (!brand) return;
    suppressClick.current = true;
    onSelectRef.current(brand);
  };

  useEffect(() => () => stopDragListeners.current(), []);

  return (
    <div
      ref={viewportRef}
      className={`wl7-finder-marquee${dragging ? ' is-dragging' : ''}${instant ? ' is-instant' : ''}`}
      aria-label="Marcas em estoque"
      onPointerDown={(event) => {
        if (event.button !== 0 || !canSlide || drag.current.active) return;
        drag.current = {
          active: true,
          pointerId: event.pointerId,
          startX: event.clientX,
          lastX: event.clientX,
          lastY: event.clientY,
          moved: false,
        };
        setDragging(true);
        setDragX(0);

        const onMove = (moveEvent: PointerEvent) => {
          if (!drag.current.active || moveEvent.pointerId !== drag.current.pointerId) return;
          if (moveEvent.pointerType === 'mouse' && moveEvent.buttons === 0) {
            endDrag(moveEvent.clientX, moveEvent.clientY, false);
            return;
          }
          drag.current.lastX = moveEvent.clientX;
          drag.current.lastY = moveEvent.clientY;
          const delta = moveEvent.clientX - drag.current.startX;
          if (Math.abs(delta) > 6) drag.current.moved = true;
          const stepNow = stepRef.current;
          const projected = -indexRef.current * stepNow + delta;
          const minTranslate = -maxTranslateIndexRef.current * stepNow;
          let next = delta;
          if (projected > 0) next = delta - projected * 0.72;
          else if (projected < minTranslate) next = delta - (projected - minTranslate) * 0.72;
          setDragX(next);
        };
        const onUp = (upEvent: PointerEvent) => {
          if (upEvent.pointerId !== drag.current.pointerId) return;
          endDrag(upEvent.clientX, upEvent.clientY, upEvent.type === 'pointerup');
        };
        const onBlur = () => {
          endDrag(drag.current.lastX, drag.current.lastY, false);
        };
        const onMouseUp = (mouseEvent: MouseEvent) => {
          if (!drag.current.active) return;
          endDrag(mouseEvent.clientX, mouseEvent.clientY, true);
        };

        stopDragListeners.current();
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        window.addEventListener('mouseup', onMouseUp);
        window.addEventListener('blur', onBlur);
        stopDragListeners.current = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
          window.removeEventListener('mouseup', onMouseUp);
          window.removeEventListener('blur', onBlur);
        };
      }}
      onClickCapture={(event) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <div
        className="wl7-finder-marquee-track"
        style={{ transform: `translate3d(${-index * step + dragX}px, 0, 0)` }}
      >
        {looped.map((brand, itemIndex) => (
          <FinderBrandButton
            key={`${brand}-${itemIndex}`}
            brand={brand}
            active={activeBrand === brand}
            hidden={canSlide && Math.floor(itemIndex / brands.length) !== 1}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

interface HeroProps {
  config: any;
}

export const Hero = ({ config }: HeroProps) => {
  const { selectedStore, availableStores } = useStore();
  const store = selectedStore || config.stores[0];
  const isMobile = useIsMobile();
  const platform = isMobile ? 'mobile' : 'web';
  const alternatePlatform = isMobile ? 'web' : 'mobile';
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterModel, setFilterModel] = useState('all');
  const [filterVersion, setFilterVersion] = useState('all');
  const [filterStockType, setFilterStockType] = useState('all');
  const [yearRange, setYearRange] = useState<[number, number] | null>(null);
  const [priceRange, setPriceRange] = useState<[number, number] | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { colors } = useLayout7Theme();
  const { vehicles, allVehicles } = useAppData();
  
  // Filter vehicles based on unifiedAds and unifiedAdsStore rules
  const filteredVehicles = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;
    
    // Rule 1: If unifiedAds is false, always filter by selected store
    if (!unifiedAds) {
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
    
    // Rule 2: If unifiedAds is true and unifiedAdsStore is null, show all vehicles
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) {
      return vehicles;
    }
    
    // Rule 3 & 4: If unifiedAds is true and unifiedAdsStore is set
    if (selectedStoreId === unifiedAdsStore) {
      // Rule 3: Selected store is the unified store → show all vehicles
      return vehicles;
    } else {
      // Rule 4: Selected store is different → filter by selected store
      if (selectedStoreId) {
        return vehicles.filter(v => v.store === selectedStoreId);
      }
      return vehicles;
    }
  }, [config?.unifiedAds, config?.unifiedAdsStore, vehicles, selectedStore?._id]);

  const heroSlides = useMemo(() => {
    const allBanners = store.banners || [];
    const platformBanners = allBanners.filter((v: any) => v.platform === platform);
    if (platformBanners.length > 0) return platformBanners;

    const crossPlatformBanners = allBanners.filter((v: any) => v.platform === alternatePlatform);
    if (crossPlatformBanners.length > 0) return crossPlatformBanners;

    const withPhoto = (list: typeof filteredVehicles) =>
      list.filter((vehicle) => vehicle.photos?.[0]);

    const featured = withPhoto(
      filteredVehicles.filter(
        (vehicle) =>
          vehicle.changed?.featured ||
          (vehicle.statistics?.views && vehicle.statistics.views > 100) ||
          (vehicle.statistics?.favorites && vehicle.statistics.favorites > 3)
      )
    );
    const source = featured.length > 0 ? featured : withPhoto(filteredVehicles);

    return source.slice(0, 5).map((vehicle) => ({
      image: vehicle.photos[0],
      title: `${vehicle.brand} ${vehicle.model}`,
      type: 'ad',
      values: [{ value: vehicle.id }],
    }));
  }, [store.banners, platform, alternatePlatform, filteredVehicles]);

  const buttonColor = colors.button;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb86a8eca108d87c91dc0': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcef498eca108d87c91dc8': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      // Agendar
      '65da42048eca108d87c91da6': 'Agendar', // Agendar Videochamada
      '65dceeac8eca108d87c91dc6': 'Agendar', // Serviços
      '65dcef098eca108d87c91dc7': 'Agendar', // Revisões
      '65dcb5e78eca108d87c91db8': 'Agendar', // Test-Drive

      // Simular
      '65dcb7898eca108d87c91dbc': 'Simular', // Financiamento

      // Avaliar
      '65dcb7548eca108d87c91dbb': 'Avaliar', // Avaliação
      '65dcb7c58eca108d87c91dbd': 'Avaliar', // Consignação

      // Cotar
      '65dcb86a8eca108d87c91dc0': 'Cotar', // Seguros
      '65dcb6528eca108d87c91db9': 'Cotar', // Despachante
      '6628fd535bda2ae51f6fc5ff': 'Cotar', // Peças e Acessórios

      // Solicitar
      '65dcef468eca108d87c91dc8': 'Solicitar', // Consórcio
      '65dcb8548eca108d87c91dbf': 'Solicitar', // Vendas Diretas

      // Consultar
      '65dcef498eca108d87c91dc8': 'Consultar', // Tabela FIPE
    };

    return buttonTexts[serviceId] || 'Solicitar Serviço';
  };

  const vehiclesByStockType = useMemo(() => {
    return filteredVehicles.filter((vehicle) => {
      if (filterStockType === 'zerokm') return vehicle.isZeroKm || vehicle.mileage === 0;
      if (filterStockType === 'seminovo') return !vehicle.isZeroKm && vehicle.mileage > 0;
      return true;
    });
  }, [filteredVehicles, filterStockType]);

  // Marcas dependentes do tipo (ex.: Seminovos sem BYD zero km)
  const brands = useMemo(
    () => Array.from(new Set(vehiclesByStockType.map((v) => v.brand))).sort(),
    [vehiclesByStockType]
  );

  const models = useMemo(() => {
    const source =
      filterBrand === 'all'
        ? vehiclesByStockType
        : vehiclesByStockType.filter((v) => v.brand === filterBrand);
    return Array.from(new Set(source.map((v) => v.model))).sort();
  }, [vehiclesByStockType, filterBrand]);

  const versions = useMemo(() => {
    if (filterModel === 'all') return [];
    const source = vehiclesByStockType.filter((vehicle) => {
      if (filterBrand !== 'all' && vehicle.brand !== filterBrand) return false;
      if (vehicle.model !== filterModel) return false;
      return Boolean(vehicle.version);
    });
    return Array.from(new Set(source.map((vehicle) => vehicle.version as string))).sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    );
  }, [vehiclesByStockType, filterBrand, filterModel]);

  const yearBounds = useMemo(() => {
    const years = vehiclesByStockType
      .map((vehicle) => Number(vehicle.modelYear))
      .filter((year) => Number.isFinite(year) && year > 1900);
    if (!years.length) return null;
    return { min: Math.min(...years), max: Math.max(...years) };
  }, [vehiclesByStockType]);

  const priceBounds = useMemo(() => {
    const prices = vehiclesByStockType
      .map((vehicle) => Number(vehicle.price))
      .filter((price) => Number.isFinite(price) && price > 0);
    if (!prices.length) return null;
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [vehiclesByStockType]);

  const shownYear: [number, number] | null =
    yearRange ?? (yearBounds ? [yearBounds.min, yearBounds.max] : null);
  const shownPrice: [number, number] | null =
    priceRange ?? (priceBounds ? [priceBounds.min, priceBounds.max] : null);

  const matchCount = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return vehiclesByStockType.filter((vehicle) => {
      if (filterBrand !== 'all' && vehicle.brand !== filterBrand) return false;
      if (filterModel !== 'all' && vehicle.model !== filterModel) return false;
      if (filterVersion !== 'all' && vehicle.version !== filterVersion) return false;
      if (
        term &&
        !`${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (shownYear && (vehicle.modelYear < shownYear[0] || vehicle.modelYear > shownYear[1])) {
        return false;
      }
      if (shownPrice && (vehicle.price < shownPrice[0] || vehicle.price > shownPrice[1])) {
        return false;
      }
      return true;
    }).length;
  }, [
    vehiclesByStockType,
    filterBrand,
    filterModel,
    filterVersion,
    searchTerm,
    shownYear,
    shownPrice,
  ]);

  const stockTypeOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: 'all', label: 'Todos' }];
    const pool =
      filterBrand === 'all'
        ? filteredVehicles
        : filteredVehicles.filter((v) => v.brand === filterBrand);
    if (store?.ad?.showZeroKm && pool.some((v) => v.isZeroKm || v.mileage === 0)) {
      options.push({ value: 'zerokm', label: 'Zero KM' });
    }
    if (store?.ad?.showUsed && pool.some((v) => !v.isZeroKm && v.mileage > 0)) {
      options.push({ value: 'seminovo', label: 'Seminovos' });
    }
    return options;
  }, [store, filteredVehicles, filterBrand]);

  // Limpa marca/modelo inválidos quando o tipo (ou estoque) muda
  useEffect(() => {
    if (filterBrand !== 'all' && !brands.includes(filterBrand)) {
      setFilterBrand('all');
      setFilterModel('all');
      return;
    }
    if (filterModel !== 'all' && !models.includes(filterModel)) {
      setFilterModel('all');
      setFilterVersion('all');
    }
  }, [brands, models, filterBrand, filterModel]);

  useEffect(() => {
    if (filterVersion !== 'all' && !versions.includes(filterVersion)) {
      setFilterVersion('all');
    }
  }, [versions, filterVersion]);

  useEffect(() => {
    if (!yearBounds) return;
    setYearRange((prev) => {
      if (!prev) return [yearBounds.min, yearBounds.max];
      const lo = Math.min(Math.max(prev[0], yearBounds.min), yearBounds.max);
      const hi = Math.max(lo, Math.min(prev[1], yearBounds.max));
      if (lo === prev[0] && hi === prev[1]) return prev;
      return [lo, hi];
    });
  }, [yearBounds]);

  useEffect(() => {
    if (!priceBounds) return;
    setPriceRange((prev) => {
      if (!prev) return [priceBounds.min, priceBounds.max];
      const lo = Math.min(Math.max(prev[0], priceBounds.min), priceBounds.max);
      const hi = Math.max(lo, Math.min(prev[1], priceBounds.max));
      if (lo === prev[0] && hi === prev[1]) return prev;
      return [lo, hi];
    });
  }, [priceBounds]);

  useEffect(() => {
    if (
      filterStockType !== 'all' &&
      !stockTypeOptions.some((opt) => opt.value === filterStockType)
    ) {
      setFilterStockType('all');
    }
  }, [stockTypeOptions, filterStockType]);

  const handleFilterSearch = () => {
    const params = new URLSearchParams();
    const brand = brands.includes(filterBrand) ? filterBrand : 'all';
    const model = brand !== 'all' && models.includes(filterModel) ? filterModel : 'all';
    const version = versions.includes(filterVersion) ? filterVersion : 'all';
    const type = stockTypeOptions.some((opt) => opt.value === filterStockType)
      ? filterStockType
      : 'all';
    if (brand !== 'all') params.set('brand', brand);
    if (model !== 'all') params.set('model', model);
    if (version !== 'all') params.set('version', version);
    if (type === 'zerokm' || type === 'seminovo') params.set('type', type);
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (
      yearBounds &&
      shownYear &&
      (shownYear[0] > yearBounds.min || shownYear[1] < yearBounds.max)
    ) {
      params.set('minYear', String(shownYear[0]));
      params.set('maxYear', String(shownYear[1]));
    }
    if (
      priceBounds &&
      shownPrice &&
      (shownPrice[0] > priceBounds.min || shownPrice[1] < priceBounds.max)
    ) {
      params.set('minPrice', String(shownPrice[0]));
      params.set('maxPrice', String(shownPrice[1]));
    }
    const qs = params.toString();
    navigate(qs ? `/estoque?${qs}` : '/estoque');
  };

  // Generate autocomplete suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];

    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any; filterKey?: string; filterValue?: string; count?: number; minYear?: number; maxYear?: number; storeId?: string }> = [];

    // Suggest stores (show ALL stores from config, including hidden ones)
    // Only show if there are multiple stores
    const allStores = config?.stores || [];
    if (allStores.length > 1) {
      allStores.forEach((store: any) => {
        if (store.company.toLowerCase().includes(term)) {
          // Use allVehicles to count all vehicles from this store
          const count = allVehicles.filter(v => v.store === store._id).length;
          if (count > 0) {
            results.push({ 
              type: 'store', 
              text: store.company, 
              filterKey: 'store', 
              filterValue: store._id,
              storeId: store._id,
              count 
            });
          }
        }
      });
    }

    // Suggest vehicle types (Carro, Moto, Caminhão)
    const vehicleTypes = [
      { text: 'Carro', value: '1' },
      { text: 'Moto', value: '2' },
      { text: 'Caminhão', value: '3' }
    ];
    vehicleTypes.forEach(({ text, value }) => {
      if (text.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.type.toString() === value).length;
        if (count > 0) {
          results.push({ type: 'category', text: text, filterKey: 'category', filterValue: value, count });
        }
      }
    });

    // Suggest brands
    brands.forEach(brand => {
      if (brand.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.brand === brand).length;
        results.push({ type: 'brand', text: brand, count });
      }
    });

    // Suggest models
    const models = Array.from(new Set(filteredVehicles.map(v => `${v.brand} ${v.model}`)));
    models.forEach(model => {
      if (model.toLowerCase().includes(term) && !results.find(s => s.text === model)) {
        const count = filteredVehicles.filter(v => `${v.brand} ${v.model}` === model).length;
        results.push({ type: 'model', text: model, count });
      }
    });

    // Suggest years (count exact, navigate with same year for both min and max)
    const years = Array.from(new Set(filteredVehicles.map(v => v.modelYear))).sort((a, b) => b - a);
    years.forEach(year => {
      if (year.toString().includes(term)) {
        // Count only vehicles with exact modelYear
        const count = filteredVehicles.filter(v => v.modelYear === year).length;

        if (count > 0) {
          // Set both minYear and maxYear to the same year
          const minYear = year;
          const maxYear = year;

          results.push({
            type: 'year',
            text: year.toString(),
            filterKey: 'yearRange',
            filterValue: `${minYear}-${maxYear}`,
            minYear,
            maxYear,
            count
          });
        }
      }
    });

    // Suggest fuels
    const fuels = Array.from(new Set(filteredVehicles.map(v => v.fuel).filter(Boolean)));
    fuels.forEach(fuel => {
      if (fuel.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.fuel === fuel).length;
        results.push({ type: 'fuel', text: fuel, filterKey: 'fuel', filterValue: fuel, count });
      }
    });

    // Suggest transmissions
    const transmissions = Array.from(new Set(filteredVehicles.map(v => v.transmission).filter(Boolean)));
    transmissions.forEach(transmission => {
      if (transmission.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.transmission === transmission).length;
        results.push({ type: 'transmission', text: transmission, filterKey: 'transmission', filterValue: transmission, count });
      }
    });

    // Suggest colors
    const colors = Array.from(new Set(filteredVehicles.map(v => v.color).filter(Boolean)));
    colors.forEach(color => {
      if (color.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.color === color).length;
        results.push({ type: 'color', text: color, filterKey: 'color', filterValue: color, count });
      }
    });

    // Suggest specific vehicles (no count needed)
    filteredVehicles.forEach(vehicle => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 15) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find(s => s.text === vehicleText)) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      }
    });

    return results.slice(0, 10); // Limit to 10 suggestions
  }, [searchTerm, filteredVehicles, brands, allVehicles, config?.stores]);

  // Reset banner index when store or banner list changes
  useEffect(() => {
    setCurrentBannerIndex(0);
  }, [store._id, heroSlides.length]);

  // Auto-rotate through all hero slides
  useEffect(() => {
    if (heroSlides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex((prev) => (prev + 1) % heroSlides.length);
    }, 9000);

    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const handleBannerChange = (index: number) => {
    if (index >= 0 && index < heroSlides.length) {
      setCurrentBannerIndex(index);
    }
  };

  const isBannerClickable = (banner: any) =>
    banner?.type && banner.type !== 'none' && banner.type !== 'null' && banner.type !== null;

  const handleBannerClick = async (banner: any) => {
    if (!banner || !banner.type) return;

    switch (banner.type) {
      case 'ad':
        // Check number of values
        if (banner.values && banner.values.length > 0) {
          if (banner.values.length === 1) {
            // Single vehicle - redirect to vehicle detail with slug
            const vehicleId = banner.values[0].value;
            const vehicle =
              vehicles.find((v) => v.id.toString() === vehicleId.toString()) ||
              filteredVehicles.find((v) => v.id.toString() === vehicleId.toString());
            if (vehicle) {
              const slug = await generateVehicleSlug(vehicle);
              navigate(`/estoque/${slug}`);
            } else {
              navigate(`/estoque/${vehicleId}`); // Fallback to ID
            }
          } else {
            // Multiple vehicles - redirect to banner vehicles page
            const vehicleIds = banner.values.map((v: any) => v.value).join(',');
            const title = encodeURIComponent(banner.title || 'Veículos em Promoção');
            navigate(`/destaques?ids=${vehicleIds}&title=${title}`);
          }
        }
        break;

      case 'link':
        // Open external link
        if (banner.values && banner.values.length > 0 && banner.values[0].value) {
          window.open(banner.values[0].value, '_blank');
        }
        break;

      case null:
      case 'null':
        // No action
        break;

      default:
        break;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleFilterSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else {
        handleFilterSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setShowSuggestions(false);

    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      // Navigate to vehicle detail with slug (await async function)
      const slug = await generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else if (suggestion.type === 'store' && suggestion.storeId) {
      // Navigate to vehicle listing filtered by store
      navigate(`/estoque?store=${suggestion.storeId}`);
    } else if (suggestion.type === 'year' && suggestion.minYear && suggestion.maxYear) {
      // Navigate with year range
      navigate(`/estoque?minYear=${suggestion.minYear}&maxYear=${suggestion.maxYear}`);
    } else if (suggestion.filterKey && suggestion.filterValue) {
      // Navigate with specific filter
      navigate(`/estoque?${suggestion.filterKey}=${encodeURIComponent(suggestion.filterValue)}`);
    } else {
      // Fill search term and navigate to vehicle listing
      setSearchTerm(suggestion.text);
      navigate(`/estoque?search=${encodeURIComponent(suggestion.text)}`);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      updateDropdownPosition();
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Close suggestions when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        searchRef.current &&
        !searchRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleScroll = (e: Event) => {
      // Não fazer nada se o scroll for dentro do próprio dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return;
      }
      // Atualizar a posição do dropdown ao invés de fechá-lo
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showSuggestions]);

  const hasHeroVisual = heroSlides.length > 0;

  const renderFinder = () => {
    const priceSpan = priceBounds ? priceBounds.max - priceBounds.min : 0;
    const priceStep =
      priceSpan > 200000 ? 1000 : priceSpan > 20000 ? 500 : priceSpan > 2000 ? 100 : 1;
    const stockValue = stockTypeOptions.some((opt) => opt.value === filterStockType)
      ? filterStockType
      : 'all';
    const brandValue = filterBrand === 'all' || brands.includes(filterBrand) ? filterBrand : 'all';
    const modelValue = filterModel === 'all' || models.includes(filterModel) ? filterModel : 'all';
    const versionValue =
      filterVersion === 'all' || versions.includes(filterVersion) ? filterVersion : 'all';

    const selectBrand = (brand: string) => {
      const params = new URLSearchParams();
      params.set('brand', brand);
      navigate(`/estoque?${params.toString()}`);
    };

    return (
      <form
        className="wl7-finder"
        onSubmit={(event) => {
          event.preventDefault();
          handleFilterSearch();
        }}
      >
        <div className="wl7-finder-head">
          <h2>Encontre seu novo carro agora!</h2>
          <div className="wl7-finder-search" ref={searchRef}>
            <label className="sr-only" htmlFor="wl7-finder-search">
              Buscar veículo
            </label>
            <input
              id="wl7-finder-search"
              ref={inputRef}
              type="text"
              placeholder="ex. Voyage 1.6"
              value={searchTerm}
              onChange={(event) => handleSearchChange(event.target.value)}
              onKeyDown={handleKeyPress}
              onFocus={() => {
                setIsSearchFocused(true);
                updateDropdownPosition();
                if (searchTerm.length >= 2) setShowSuggestions(true);
              }}
              onBlur={() => setIsSearchFocused(false)}
              autoComplete="off"
            />
            <button type="submit" aria-label="Buscar">
              <Search className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="wl7-finder-body">
          {brands.length > 0 && (
            <FinderBrandCarousel
              brands={brands}
              activeBrand={brandValue}
              onSelect={selectBrand}
            />
          )}

          <div className="wl7-finder-prefs">
            <p className="wl7-finder-prefs-label">Escolha suas preferências:</p>
            <div className="wl7-finder-prefs-row">
              {stockTypeOptions.length > 1 && (
                <label className="wl7-finder-field">
                  <select
                    className={`wl7-finder-select${stockValue === 'all' ? ' is-placeholder' : ''}`}
                    value={stockValue}
                    onChange={(event) => {
                      setFilterStockType(event.target.value);
                      setFilterBrand('all');
                      setFilterModel('all');
                      setFilterVersion('all');
                    }}
                    aria-label="Tipo de veículo"
                  >
                    {stockTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.value === 'all' ? 'Tipo' : opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="wl7-finder-field">
                <select
                  className={`wl7-finder-select${brandValue === 'all' ? ' is-placeholder' : ''}`}
                  value={brandValue}
                  onChange={(event) => {
                    setFilterBrand(event.target.value);
                    setFilterModel('all');
                    setFilterVersion('all');
                  }}
                  aria-label="Marca"
                >
                  <option value="all">Marca</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </label>

              <label className="wl7-finder-field">
                <select
                  className={`wl7-finder-select${modelValue === 'all' ? ' is-placeholder' : ''}`}
                  value={modelValue}
                  onChange={(event) => {
                    setFilterModel(event.target.value);
                    setFilterVersion('all');
                  }}
                  disabled={models.length === 0}
                  aria-label="Modelo"
                >
                  <option value="all">Modelo</option>
                  {models.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <label className="wl7-finder-field">
                <select
                  className={`wl7-finder-select${versionValue === 'all' ? ' is-placeholder' : ''}`}
                  value={versionValue}
                  onChange={(event) => setFilterVersion(event.target.value)}
                  disabled={versions.length === 0}
                  aria-label="Versão"
                >
                  <option value="all">Versão</option>
                  {versions.map((version) => (
                    <option key={version} value={version}>
                      {version}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="submit"
                className="wl7-finder-go"
                aria-label={`Buscar ${matchCount} ${matchCount === 1 ? 'veículo' : 'veículos'}`}
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {(yearBounds || priceBounds) && (
            <div className="wl7-finder-ranges">
              {shownYear && yearBounds && (
                <div className="wl7-finder-range">
                  <span className="wl7-finder-range-label" id="wl7-finder-year-label">
                    Ano do Modelo:
                  </span>
                  <div className="wl7-finder-range-values">
                    <span className="wl7-finder-range-box">{shownYear[0]}</span>
                    <span className="wl7-finder-range-dash" aria-hidden="true">
                      -
                    </span>
                    <span className="wl7-finder-range-box">{shownYear[1]}</span>
                  </div>
                  {yearBounds.min < yearBounds.max && (
                    <Slider
                      min={yearBounds.min}
                      max={yearBounds.max}
                      step={1}
                      value={shownYear}
                      onValueChange={(value) => {
                        if (value.length === 2) setYearRange([value[0], value[1]]);
                      }}
                      className="wl7-finder-slider"
                      aria-labelledby="wl7-finder-year-label"
                    />
                  )}
                </div>
              )}

              {shownPrice && priceBounds && (
                <div className="wl7-finder-range">
                  <span className="wl7-finder-range-label" id="wl7-finder-price-label">
                    Faixa de Preço:
                  </span>
                  <div className="wl7-finder-range-values">
                    <span className="wl7-finder-range-prefix">R$</span>
                    <span className="wl7-finder-range-box">{formatFinderPrice(shownPrice[0])}</span>
                    <span className="wl7-finder-range-dash" aria-hidden="true">
                      -
                    </span>
                    <span className="wl7-finder-range-prefix">R$</span>
                    <span className="wl7-finder-range-box">{formatFinderPrice(shownPrice[1])}</span>
                  </div>
                  {priceBounds.min < priceBounds.max && (
                    <Slider
                      min={priceBounds.min}
                      max={priceBounds.max}
                      step={priceStep}
                      value={shownPrice}
                      onValueChange={(value) => {
                        if (value.length === 2) setPriceRange([value[0], value[1]]);
                      }}
                      className="wl7-finder-slider"
                      aria-labelledby="wl7-finder-price-label"
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    );
  };

  const renderBannerSlides = () =>
    heroSlides.map((banner: any, index: number) => (
      <button
        key={`${banner.image}-${index}`}
        type="button"
        className={`wl7-boxcar-hero-slide transition-opacity duration-1000 ease-in-out ${
          index === currentBannerIndex ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
        } ${isBannerClickable(banner) ? 'cursor-pointer' : 'cursor-default'}`}
        onClick={() => isBannerClickable(banner) && handleBannerClick(banner)}
        aria-label={banner.title || `Banner ${index + 1}`}
        aria-hidden={index !== currentBannerIndex}
        tabIndex={index === currentBannerIndex ? 0 : -1}
      >
        <img
          src={banner.image}
          alt={banner.title || `Banner ${index + 1}`}
          className="wl7-boxcar-hero-img"
          loading={index === 0 ? 'eager' : 'lazy'}
          decoding="async"
          draggable={false}
        />
      </button>
    ));

  const bannerNav =
    heroSlides.length > 1 ? (
      <>
        <button
          type="button"
          className="wl7-boxcar-hero-nav wl7-boxcar-hero-nav--prev"
          onClick={() => handleBannerChange(currentBannerIndex === 0 ? heroSlides.length - 1 : currentBannerIndex - 1)}
          aria-label="Banner anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="wl7-boxcar-hero-nav wl7-boxcar-hero-nav--next"
          onClick={() => handleBannerChange((currentBannerIndex + 1) % heroSlides.length)}
          aria-label="Próximo banner"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </>
    ) : null;

  return (
    <section
      id="inicio"
      className={`wl7-boxcar-hero${hasHeroVisual ? ' wl7-boxcar-hero--banner' : ' wl7-boxcar-hero--no-banner'}`}
    >
      {hasHeroVisual && isMobile && (
        <div className="wl7-boxcar-hero-banner relative wl7-boxcar-hero-banner--mobile">
          {renderBannerSlides()}
          <div className="absolute inset-0 wl7-boxcar-hero-fade z-[2] pointer-events-none" />
        </div>
      )}

      {hasHeroVisual && !isMobile && (
        <div className="wl7-boxcar-hero-banner full-banner full-banner--card full-banner--layout3 relative full-banner--desktop">
          <div className="full-banner__frame">
            {renderBannerSlides()}
            <div className="absolute inset-0 wl7-boxcar-hero-fade z-[2] pointer-events-none" />
            {bannerNav}
          </div>
        </div>
      )}

      <div className="wl7-container wl7-boxcar-hero-shell">
        {!hasHeroVisual && (
          <div className="wl7-boxcar-hero-frame wl7-boxcar-hero-frame--compact">
            <div className="wl7-boxcar-hero-intro">
              <h1 className="wl7-boxcar-hero-intro-title">{config.name}</h1>
              {filteredVehicles.length > 0 && (
                <p className="wl7-boxcar-hero-intro-subtitle">
                  {filteredVehicles.length}{' '}
                  {filteredVehicles.length === 1 ? 'veículo disponível' : 'veículos disponíveis'}
                </p>
              )}
            </div>
          </div>
        )}
        {renderFinder()}
      </div>

      {/* Autocomplete portal — preserved for search integrations */}
      {showSuggestions && suggestions.length > 0 && dropdownPosition.width > 0 && createPortal(
        <div
          ref={dropdownRef}
          className="absolute bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-2xl max-h-[400px] overflow-y-auto"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 40,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              className={`group w-full text-left px-4 py-3 transition-all duration-200 border-b border-border/30 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${index === selectedSuggestionIndex
                ? 'bg-accent/10 border-l-2 border-l-accent pl-[14px]'
                : 'hover:bg-muted/50 pl-[14px'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {suggestion.type === 'category' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center ring-1 ring-purple-500/20 transition-all duration-200">
                      {suggestion.text === 'Carro' && <Car className="h-4 w-4 text-purple-500" />}
                      {suggestion.text === 'Moto' && <Bike className="h-4 w-4 text-purple-500" />}
                      {suggestion.text === 'Caminhão' && <Truck className="h-4 w-4 text-purple-500" />}
                    </div>
                  )}
                  {suggestion.type === 'store' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600/20 to-blue-600/5 flex items-center justify-center ring-1 ring-blue-600/20 transition-all duration-200">
                      <Building2 className="h-4 w-4 text-blue-600" />
                    </div>
                  )}
                  {suggestion.type === 'brand' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ring-1 ring-primary/20 transition-all duration-200">
                      <Search className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  {suggestion.type === 'model' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center ring-1 ring-blue-500/20 transition-all duration-200">
                      <Car className="h-4 w-4 text-blue-500" />
                    </div>
                  )}
                  {suggestion.type === 'year' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center ring-1 ring-green-500/20 transition-all duration-200">
                      <Calendar className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                  {suggestion.type === 'fuel' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-500/5 flex items-center justify-center ring-1 ring-orange-500/20 transition-all duration-200">
                      <Fuel className="h-4 w-4 text-orange-500" />
                    </div>
                  )}
                  {suggestion.type === 'transmission' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/20 to-indigo-500/5 flex items-center justify-center ring-1 ring-indigo-500/20 transition-all duration-200">
                      <Settings className="h-4 w-4 text-indigo-500" />
                    </div>
                  )}
                  {suggestion.type === 'color' && (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500/20 to-pink-500/5 flex items-center justify-center ring-1 ring-pink-500/20 transition-all duration-200">
                      <Palette className="h-4 w-4 text-pink-500" />
                    </div>
                  )}
                  {suggestion.type === 'vehicle' && suggestion.vehicle && (
                    <div className="relative w-14 h-14 rounded-lg overflow-hidden ring-1 ring-border/50 transition-all duration-200">
                      <img
                        src={suggestion.vehicle.photos[0]}
                        alt={suggestion.text}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate group-hover:text-accent-foreground transition-colors">
                        {suggestion.text}
                      </div>

                      <div className="flex items-center gap-2 mt-0.5">
                        {/* Type Badge - Below text */}
                        {suggestion.type === 'category' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-purple-500/10 text-purple-500 rounded-full">
                            Tipo
                          </span>
                        )}
                        {suggestion.type === 'store' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-600/10 text-blue-600 rounded-full">
                            Loja
                          </span>
                        )}
                        {suggestion.type === 'brand' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded-full">
                            Marca
                          </span>
                        )}
                        {suggestion.type === 'model' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-500 rounded-full">
                            Modelo
                          </span>
                        )}
                        {suggestion.type === 'year' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500/10 text-green-500 rounded-full">
                            Ano
                          </span>
                        )}
                        {suggestion.type === 'fuel' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-orange-500/10 text-orange-500 rounded-full">
                            Combustível
                          </span>
                        )}
                        {suggestion.type === 'transmission' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-500 rounded-full">
                            Câmbio
                          </span>
                        )}
                        {suggestion.type === 'color' && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-pink-500/10 text-pink-500 rounded-full">
                            Cor
                          </span>
                        )}

                        {/* Additional Info */}
                        {suggestion.type === 'vehicle' && suggestion.vehicle && (
                          <>
                            <span className="font-medium">{suggestion.vehicle.modelYear}</span>
                            <span className="text-border">•</span>
                            <span className="font-bold text-foreground">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(suggestion.vehicle.price)}
                            </span>
                          </>
                        )}
                        {/* {suggestion.type === 'year' && suggestion.minYear && suggestion.maxYear && (
                          <span className="text-muted-foreground/70">
                            {suggestion.minYear}-{suggestion.maxYear}
                          </span>
                        )} */}
                      </div>
                    </div>

                    {/* Count Badge - Right Side */}
                    {suggestion.type !== 'vehicle' && suggestion.count !== undefined && (
                      <div className="flex-shrink-0">
                        <span 
                          className="inline-flex items-center justify-center min-w-[28px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-muted-foreground/20 text-muted-foreground">
                          {suggestion.count}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </section >
  );
};